/**
 * Ventanas de **Finanzas → Pagos de Operadores**: verificar un pago, verificar un
 * lote y la ficha del movimiento bancario.
 *
 * Son las cuatro pantallas del panel anterior (`verify.php`, `verify_batch.php` y
 * `view_movement.php`, más el listado): la individual va pago a pago y la masiva
 * concilia de una vez todo un depósito del banco, con el monto **real** tecleado.
 *
 * Reglas heredadas:
 * - El **monto bruto** del pago no se escribe: es el de la venta.
 * - La comisión sale del **operador elegido** (su `commission_rate`) y se enseña en
 *   vivo al cambiar de operador, como la tarjeta de comisión del original.
 * - En el lote el movimiento se registra por el **monto real** y la diferencia
 *   contra el neto esperado se muestra mientras se escribe, sin bloquear.
 * - El comprobante es opcional (PDF, PNG o JPG de hasta 5 MB).
 */

import { Ban, FileText, Paperclip } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { ArchivoInput } from '@/components/ui/archivo-input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CampoFecha } from '@/components/ui/calendario';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { cn, formatCurrency, formatDateValue } from '@/lib/utils';

import { pagosApi } from '../pagos-operadores.api';
import type {
  CuentaPago,
  MetodoPagoCatalogo,
  MovimientoPago,
  MovimientoPagoRespuesta,
  OperadorPago,
  PagoOperador,
  PagoRespuesta,
} from '../pagos-operadores.types';

const CONTROL =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const ACEPTADOS = 'application/pdf,image/png,image/jpeg';

/** Importe con dos decimales. */
function dinero(valor: number): number {
  return Math.round((Number(valor) || 0) * 100) / 100;
}

/** Un dato de la ficha, con su etiqueta. */
function Dato({ etiqueta, valor }: { etiqueta: string; valor: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
        {etiqueta}
      </span>
      <span className="text-sm break-words text-ink-800">{valor}</span>
    </div>
  );
}

/** Los datos del pago, que se repiten en la verificación. */
function DatosDelPago({ pago }: { pago: PagoOperador | PagoRespuesta['venta'] }) {
  return (
    <section className="rounded-card border border-ink-100 p-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Dato etiqueta="Documento" valor={pago.documento ?? '—'} />
        <Dato etiqueta="Tipo" valor={<Badge variant="brand">{pago.tipo_venta_etiqueta}</Badge>} />
        <Dato etiqueta="Turno" valor={pago.turno ? `#${pago.turno}` : '—'} />
        <Dato etiqueta="Fecha" valor={formatDateValue(pago.fecha_venta)} />
        <Dato etiqueta="Hora" valor={pago.hora ?? '—'} />
        <Dato etiqueta="Procesador" valor={pago.metodo_pago_original ?? '—'} />
        <Dato etiqueta="Referencia" valor={pago.referencia_pago || '—'} />
        <Dato
          etiqueta="Monto del pago"
          valor={
            <span className="font-bold text-ink-900 tabular-nums">
              {formatCurrency(pago.monto)}
            </span>
          }
        />
        <div className="sm:col-span-2 lg:col-span-1">
          <Dato
            etiqueta="Cliente"
            valor={
              <>
                {pago.cliente}
                {pago.cliente_documento ? (
                  <span className="text-ink-500"> · {pago.cliente_documento}</span>
                ) : null}
              </>
            }
          />
        </div>
      </div>
    </section>
  );
}

/** La tarjeta de comisión que se recalcula al elegir el operador. */
function ResumenComision({
  monto,
  operador,
  real,
}: {
  monto: number;
  operador: OperadorPago | undefined;
  /** Monto real tecleado (sólo en la verificación en lote). */
  real?: string;
}) {
  const tasa = operador?.tasa ?? 0;
  const comision = dinero((monto * tasa) / 100);
  const neto = dinero(monto - comision);
  const realNumero = real === undefined || real.trim() === '' ? null : dinero(Number(real.replace(',', '.')));
  const diferencia = realNumero === null ? null : dinero(realNumero - neto);

  return (
    <section className="rounded-card border border-brand-100 bg-brand-50/40 p-4">
      <h3 className="text-sm font-semibold text-ink-800">
        Cálculo del sistema{' '}
        {operador ? <span className="text-ink-500">· {operador.nombre} al {tasa}%</span> : null}
      </h3>

      <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Dato etiqueta="Monto bruto" valor={formatCurrency(monto)} />
        <Dato
          etiqueta={`Comisión (${tasa}%)`}
          valor={<span className="text-rose-600">{formatCurrency(comision)}</span>}
        />
        <Dato
          etiqueta="Neto esperado"
          valor={<span className="font-semibold text-emerald-700">{formatCurrency(neto)}</span>}
        />
        {diferencia === null ? (
          <Dato etiqueta="Diferencia" valor={<span className="text-ink-400">—</span>} />
        ) : (
          <Dato
            etiqueta="Diferencia"
            valor={
              <span
                className={cn(
                  'font-semibold',
                  Math.abs(diferencia) < 0.01
                    ? 'text-emerald-700'
                    : diferencia > 0
                      ? 'text-amber-700'
                      : 'text-rose-700',
                )}
              >
                {formatCurrency(diferencia)}
              </span>
            }
          />
        )}
      </div>

      {diferencia !== null && Math.abs(diferencia) >= 0.01 ? (
        <p className="mt-2 text-xs text-amber-700">
          El monto real no coincide con el neto esperado; el movimiento se registrará por el monto
          real y la diferencia quedará guardada en su detalle.
        </p>
      ) : null}
    </section>
  );
}

/** Verificación de un pago. */
export function DialogoVerificarPago({
  pago,
  venta,
  cargando,
  hoy,
  guardando,
  error,
  onCerrar,
  onVerificar,
}: {
  pago: PagoOperador | null;
  venta: PagoRespuesta | undefined;
  cargando: boolean;
  hoy: string;
  guardando: boolean;
  error: string | null;
  onCerrar: () => void;
  onVerificar: (datos: {
    operador_id: string;
    cuenta_id: string;
    metodo_pago_id: string;
    fecha: string;
    referencia: string;
    notas: string;
    comprobante: File | null;
  }) => void;
}) {
  const [operador, setOperador] = useState('');
  const [cuenta, setCuenta] = useState('');
  const [metodo, setMetodo] = useState('');
  const [fecha, setFecha] = useState(hoy);
  const [referencia, setReferencia] = useState('');
  const [notas, setNotas] = useState('');
  const [comprobante, setComprobante] = useState<File | null>(null);

  const operadores = venta?.operadores ?? [];
  const elegido = operadores.find((item) => String(item.id) === operador);
  const puedeGuardar = Boolean(operador && cuenta && metodo && fecha) && !guardando;

  return (
    <Dialog
      open={Boolean(pago)}
      onClose={onCerrar}
      title="Verificar pago del operador"
      description={
        pago ? `${pago.tipo_venta_etiqueta} ${pago.documento ?? ''} · ${pago.cliente}` : undefined
      }
      size="lg"
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!puedeGuardar}
            onClick={() =>
              onVerificar({
                operador_id: operador,
                cuenta_id: cuenta,
                metodo_pago_id: metodo,
                fecha,
                referencia,
                notas,
                comprobante,
              })
            }
          >
            Verificar pago
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? (
          <Alert variant="error" title="No se pudo verificar">
            {error}
          </Alert>
        ) : null}

        {cargando ? (
          <div className="space-y-2">
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : venta ? (
          <DatosDelPago pago={venta.venta} />
        ) : null}

        <ResumenComision monto={venta?.venta.monto ?? pago?.monto ?? 0} operador={elegido} />

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-700" htmlFor="pago-operador">
              Operador *
            </label>
            <Select
              id="pago-operador"
              value={operador}
              onChange={(evento) => setOperador(evento.target.value)}
              className={CONTROL}
              aria-label="Operador"
            >
              <option value="">Seleccionar…</option>
              {operadores.map((item) => (
                <option key={item.id} value={String(item.id)}>
                  {item.etiqueta}
                </option>
              ))}
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-700" htmlFor="pago-cuenta">
              Cuenta bancaria de destino *
            </label>
            <Select
              id="pago-cuenta"
              value={cuenta}
              onChange={(evento) => setCuenta(evento.target.value)}
              className={CONTROL}
              aria-label="Cuenta bancaria"
            >
              <option value="">Seleccionar…</option>
              {(venta?.cuentas ?? []).map((item) => (
                <option key={item.id} value={String(item.id)}>
                  {item.etiqueta}
                </option>
              ))}
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-700" htmlFor="pago-metodo">
              Método de pago *
            </label>
            <Select
              id="pago-metodo"
              value={metodo}
              onChange={(evento) => setMetodo(evento.target.value)}
              className={CONTROL}
              aria-label="Método de pago"
            >
              <option value="">Seleccionar…</option>
              {(venta?.metodos_pago ?? []).map((item) => (
                <option key={item.id} value={String(item.id)}>
                  {item.nombre}
                </option>
              ))}
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-700" htmlFor="pago-fecha">
              Fecha de verificación *
            </label>
            <CampoFecha
              id="pago-fecha"
              tipo="date"
              value={fecha}
              onChange={(evento) => setFecha(evento.target.value)}
              aria-label="Fecha de verificación"
            />
          </div>

          <Input
            label="Número de operación (opcional)"
            value={referencia}
            maxLength={255}
            placeholder="Nro. de operación del banco"
            onChange={(evento) => setReferencia(evento.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink-700" htmlFor="pago-notas">
            Observaciones (opcional)
          </label>
          <textarea
            id="pago-notas"
            rows={3}
            maxLength={1000}
            className={cn(CONTROL, 'h-auto py-2')}
            value={notas}
            onChange={(evento) => setNotas(evento.target.value)}
          />
        </div>

        <ArchivoInput
          label="Comprobante (opcional)"
          accept={ACEPTADOS}
          nombre={comprobante?.name ?? null}
          hint="PDF, PNG, JPG o JPEG de hasta 5 MB. El movimiento se registra por el neto depositado."
          onSeleccionar={(elegido) => setComprobante(elegido?.archivo ?? null)}
        />
      </div>
    </Dialog>
  );
}

/** Verificación en lote. */
export function DialogoVerificarLote({
  abierto,
  seleccionados,
  montoBruto,
  operadores,
  cuentas,
  metodos,
  hoy,
  tope,
  guardando,
  error,
  onCerrar,
  onVerificar,
}: {
  abierto: boolean;
  seleccionados: PagoOperador[];
  montoBruto: number;
  operadores: OperadorPago[];
  cuentas: CuentaPago[];
  metodos: MetodoPagoCatalogo[];
  hoy: string;
  tope: number;
  guardando: boolean;
  error: string | null;
  onCerrar: () => void;
  onVerificar: (datos: {
    operador_id: string;
    cuenta_id: string;
    metodo_pago_id: string;
    fecha: string;
    monto_real: string;
    referencia: string;
    notas: string;
    comprobante: File | null;
  }) => void;
}) {
  const [operador, setOperador] = useState('');
  const [cuenta, setCuenta] = useState('');
  const [metodo, setMetodo] = useState('');
  const [fecha, setFecha] = useState(hoy);
  const [montoReal, setMontoReal] = useState('');
  const [referencia, setReferencia] = useState('');
  const [notas, setNotas] = useState('');
  const [comprobante, setComprobante] = useState<File | null>(null);

  const elegido = operadores.find((item) => String(item.id) === operador);

  /** Comisión por operador del pago (lo que cobra cada operadora). */
  const porOperador = useMemo(() => {
    const mapa = new Map<number, { operador: string; tasa: number; pagos: number; bruto: number }>();

    for (const pago of seleccionados) {
      const clave = pago.operador_id ?? 0;
      const actual = mapa.get(clave) ?? {
        operador: pago.operador ?? 'Sin operador',
        tasa: pago.operador_tasa ?? 0,
        pagos: 0,
        bruto: 0,
      };

      actual.pagos += 1;
      actual.bruto = dinero(actual.bruto + pago.monto);
      mapa.set(clave, actual);
    }

    return [...mapa.values()].sort((a, b) => b.bruto - a.bruto);
  }, [seleccionados]);

  const puedeGuardar =
    seleccionados.length > 0 &&
    seleccionados.length <= tope &&
    Boolean(operador && cuenta && metodo && fecha && montoReal.trim()) &&
    !guardando;

  return (
    <Dialog
      open={abierto}
      onClose={onCerrar}
      title="Verificar lote de pagos"
      description={`${seleccionados.length} pago(s) seleccionados · ${formatCurrency(montoBruto)} de monto bruto`}
      size="xl"
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!puedeGuardar}
            onClick={() =>
              onVerificar({
                operador_id: operador,
                cuenta_id: cuenta,
                metodo_pago_id: metodo,
                fecha,
                monto_real: montoReal,
                referencia,
                notas,
                comprobante,
              })
            }
          >
            Verificar lote
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? (
          <Alert variant="error" title="No se pudo verificar el lote">
            {error}
          </Alert>
        ) : null}

        {seleccionados.length === 0 ? (
          <Alert variant="warning" title="No hay pagos seleccionados">
            Marca los pagos del listado (o usa «seleccionar todo lo filtrado») antes de continuar.
          </Alert>
        ) : null}

        {seleccionados.length > tope ? (
          <Alert variant="warning" title="Demasiados pagos">
            El máximo por lote es de {tope} pagos; quita algunos de la selección.
          </Alert>
        ) : null}

        <ResumenComision
          monto={montoBruto}
          operador={elegido}
          real={montoReal}
        />

        {porOperador.length > 0 ? (
          <section className="overflow-auto rounded-card border border-ink-100">
            <table className="w-full border-collapse text-[13px]">
              <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                  <th className="px-2 py-2">Operador del pago</th>
                  <th className="px-2 py-2 text-right">%</th>
                  <th className="px-2 py-2 text-right">Pagos</th>
                  <th className="px-2 py-2 text-right">Monto bruto</th>
                </tr>
              </thead>
              <tbody>
                {porOperador.map((fila) => (
                  <tr key={fila.operador} className="border-t border-ink-100">
                    <td className="px-2 py-2 font-semibold text-ink-800">{fila.operador}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{fila.tasa}%</td>
                    <td className="px-2 py-2 text-right tabular-nums">{fila.pagos}</td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      {formatCurrency(fila.bruto)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Monto real en cuenta *"
            type="number"
            step="0.01"
            min="0"
            value={montoReal}
            placeholder="Lo que ingresó al banco"
            onChange={(evento) => setMontoReal(evento.target.value)}
          />

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-700" htmlFor="lote-operador">
              Operador de la liquidación *
            </label>
            <Select
              id="lote-operador"
              value={operador}
              onChange={(evento) => setOperador(evento.target.value)}
              className={CONTROL}
              aria-label="Operador de la liquidación"
            >
              <option value="">Seleccionar…</option>
              {operadores.map((item) => (
                <option key={item.id} value={String(item.id)}>
                  {item.etiqueta}
                </option>
              ))}
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-700" htmlFor="lote-fecha">
              Fecha del depósito *
            </label>
            <CampoFecha
              id="lote-fecha"
              tipo="date"
              value={fecha}
              onChange={(evento) => setFecha(evento.target.value)}
              aria-label="Fecha del depósito"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-700" htmlFor="lote-cuenta">
              Cuenta bancaria *
            </label>
            <Select
              id="lote-cuenta"
              value={cuenta}
              onChange={(evento) => setCuenta(evento.target.value)}
              className={CONTROL}
              aria-label="Cuenta bancaria"
            >
              <option value="">Seleccionar…</option>
              {cuentas.map((item) => (
                <option key={item.id} value={String(item.id)}>
                  {item.etiqueta}
                </option>
              ))}
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-700" htmlFor="lote-metodo">
              Método de ingreso *
            </label>
            <Select
              id="lote-metodo"
              value={metodo}
              onChange={(evento) => setMetodo(evento.target.value)}
              className={CONTROL}
              aria-label="Método de ingreso"
            >
              <option value="">Seleccionar…</option>
              {metodos.map((item) => (
                <option key={item.id} value={String(item.id)}>
                  {item.nombre}
                </option>
              ))}
            </Select>
          </div>

          <Input
            label="N° de operación bancaria (opcional)"
            value={referencia}
            maxLength={255}
            onChange={(evento) => setReferencia(evento.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink-700" htmlFor="lote-notas">
            Observaciones (opcional)
          </label>
          <textarea
            id="lote-notas"
            rows={3}
            maxLength={1000}
            className={cn(CONTROL, 'h-auto py-2')}
            value={notas}
            onChange={(evento) => setNotas(evento.target.value)}
          />
        </div>

        <ArchivoInput
          label="Comprobante del depósito (opcional)"
          accept={ACEPTADOS}
          nombre={comprobante?.name ?? null}
          hint="La comisión del lote se calcula con el operador de la liquidación; el monto real es el que se registra en el movimiento."
          onSeleccionar={(elegido) => setComprobante(elegido?.archivo ?? null)}
        />

        {seleccionados.length > 0 && seleccionados.length <= 20 ? (
          <section className="overflow-auto rounded-card border border-ink-100">
            <table className="w-full border-collapse text-[13px]">
              <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                  <th className="px-2 py-2">Documento</th>
                  <th className="px-2 py-2">Cliente</th>
                  <th className="px-2 py-2">Fecha</th>
                  <th className="px-2 py-2 text-right">Monto</th>
                </tr>
              </thead>
              <tbody>
                {seleccionados.map((pago) => (
                  <tr key={pago.pago_id} className="border-t border-ink-100">
                    <td className="px-2 py-2 font-semibold text-ink-800">{pago.documento}</td>
                    <td className="max-w-[16rem] truncate px-2 py-2 text-ink-600">
                      {pago.cliente}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      {formatDateValue(pago.fecha_venta)}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      {formatCurrency(pago.monto)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : null}
      </div>
    </Dialog>
  );
}

/** Ficha del movimiento bancario de una verificación (individual o en lote). */
export function DialogoMovimientoPago({
  movimientoId,
  consulta,
  cargando,
  guardando,
  error,
  onCerrar,
  onAnular,
}: {
  movimientoId: number | null;
  consulta: MovimientoPagoRespuesta | undefined;
  cargando: boolean;
  guardando: boolean;
  error: string | null;
  onCerrar: () => void;
  onAnular: (movimiento: MovimientoPago) => void;
}) {
  const movimiento = consulta?.movimiento;
  const [errorComprobante, setErrorComprobante] = useState<string | null>(null);

  const abrirComprobante = async () => {
    if (!movimientoId) return;

    setErrorComprobante(null);

    try {
      await abrirPdfAutenticado(pagosApi.urlComprobante(movimientoId));
    } catch (fallo) {
      setErrorComprobante(
        fallo instanceof Error ? fallo.message : 'No se pudo abrir el comprobante.',
      );
    }
  };

  return (
    <Dialog
      open={Boolean(movimientoId)}
      onClose={onCerrar}
      title={movimientoId ? `Movimiento #${movimientoId}` : 'Movimiento'}
      description={
        movimiento
          ? `${movimiento.tipo} · ${movimiento.banco} · ${movimiento.cuenta}`
          : undefined
      }
      size="xl"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <div>
            {movimiento?.puede_anular ? (
              <Button
                variant="danger"
                size="sm"
                leftIcon={<Ban className="size-3.5" aria-hidden="true" />}
                disabled={guardando}
                onClick={() => onAnular(movimiento)}
              >
                Anular y liberar
              </Button>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {movimiento?.comprobante?.disponible ? (
              <Button
                variant="outline"
                size="sm"
                leftIcon={<Paperclip className="size-3.5" aria-hidden="true" />}
                onClick={() => void abrirComprobante()}
              >
                Ver comprobante
              </Button>
            ) : null}

            <Button variant="outline" size="sm" onClick={onCerrar}>
              Cerrar
            </Button>
          </div>
        </div>
      }
    >
      {cargando ? (
        <div className="space-y-2">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : (
        <div className="space-y-4">
          {error ? (
            <Alert variant="error" title="No se pudo completar la acción">
              {error}
            </Alert>
          ) : null}

          {errorComprobante ? (
            <Alert variant="error" title="No se pudo abrir el comprobante">
              {errorComprobante}
            </Alert>
          ) : null}

          {movimiento?.anulado ? (
            <Alert variant="warning" title="Movimiento anulado">
              Lo anuló {movimiento.anulado_por ?? '—'}
              {movimiento.anulado_el ? ` el ${movimiento.anulado_el}` : ''}. Sus pagos volvieron a
              quedar pendientes de verificar.
            </Alert>
          ) : null}

          {movimiento ? (
            <>
              <section className="rounded-card border border-ink-100 p-4">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <Dato etiqueta="Estado" valor={<Badge variant="brand">{movimiento.estado}</Badge>} />
                  <Dato etiqueta="Verificación" valor={movimiento.verificacion} />
                  <Dato etiqueta="Fecha del depósito" valor={formatDateValue(movimiento.fecha)} />
                  <Dato
                    etiqueta="Monto registrado"
                    valor={
                      <span className="font-bold text-emerald-700 tabular-nums">
                        {formatCurrency(movimiento.monto)}
                      </span>
                    }
                  />
                  <Dato etiqueta="Operador" valor={movimiento.detalle.operator_name ?? '—'} />
                  <Dato
                    etiqueta="Tasa"
                    valor={movimiento.tasa === null ? '—' : `${movimiento.tasa}%`}
                  />
                  <Dato etiqueta="Banco" valor={movimiento.banco} />
                  <Dato
                    etiqueta="Cuenta"
                    valor={
                      movimiento.cuenta_numero
                        ? `${movimiento.cuenta} · ${movimiento.cuenta_numero}`
                        : movimiento.cuenta
                    }
                  />
                  <Dato etiqueta="Método de pago" valor={movimiento.metodo_pago} />
                  <Dato etiqueta="Referencia" valor={movimiento.referencia ?? '—'} />
                  <Dato etiqueta="Registrado" valor={movimiento.registrado ?? '—'} />
                  <Dato etiqueta="Registró" valor={movimiento.ejecutor} />
                </div>
              </section>

              <section className="rounded-card border border-brand-100 bg-brand-50/40 p-4">
                <h3 className="text-sm font-semibold text-ink-800">Conciliación del depósito</h3>

                <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                  <Dato etiqueta="Monto bruto" valor={formatCurrency(movimiento.bruto)} />
                  <Dato
                    etiqueta="(−) Comisión"
                    valor={
                      <span className="text-rose-600">{formatCurrency(movimiento.comision)}</span>
                    }
                  />
                  <Dato etiqueta="Neto esperado" valor={formatCurrency(movimiento.neto_esperado)} />
                  <Dato
                    etiqueta="Monto real ingresado"
                    valor={
                      <span className="font-semibold text-emerald-700">
                        {formatCurrency(movimiento.monto_real)}
                      </span>
                    }
                  />
                  <Dato
                    etiqueta="Diferencia"
                    valor={
                      <span
                        className={cn(
                          'font-semibold',
                          Math.abs(movimiento.diferencia) < 0.01
                            ? 'text-emerald-700'
                            : 'text-amber-700',
                        )}
                      >
                        {formatCurrency(movimiento.diferencia)}
                      </span>
                    }
                  />
                </div>
              </section>

              {movimiento.pagos.length > 0 ? (
                <section>
                  <h3 className="mb-2 text-sm font-semibold text-ink-800">
                    Pagos dentro del movimiento ({movimiento.pagos.length})
                  </h3>

                  <div className="overflow-auto rounded-card border border-ink-100">
                    <table className="w-full border-collapse text-[13px]">
                      <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                        <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                          <th className="px-2 py-2">Documento</th>
                          <th className="px-2 py-2">Cliente</th>
                          <th className="px-2 py-2 text-right">Monto</th>
                          <th className="px-2 py-2 text-right">Comisión</th>
                          <th className="px-2 py-2 text-right">Neto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {movimiento.pagos.map((pago, indice) => (
                          <tr key={`${pago.sale_id}-${indice}`} className="border-t border-ink-100">
                            <td className="px-2 py-2 font-semibold text-ink-800">
                              {pago.document ?? '—'}
                            </td>
                            <td className="max-w-[16rem] truncate px-2 py-2 text-ink-600">
                              {pago.client ?? '—'}
                            </td>
                            <td className="px-2 py-2 text-right tabular-nums">
                              {formatCurrency(pago.amount ?? 0)}
                            </td>
                            <td className="px-2 py-2 text-right tabular-nums text-rose-600">
                              {formatCurrency(pago.commission ?? 0)}
                            </td>
                            <td className="px-2 py-2 text-right font-semibold tabular-nums text-emerald-700">
                              {formatCurrency(pago.net ?? 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ) : null}

              {movimiento.observaciones ? (
                <section className="rounded-card border border-ink-100 p-4">
                  <h3 className="text-sm font-semibold text-ink-800">Observaciones</h3>
                  <p className="mt-1 flex items-start gap-2 text-sm text-ink-600">
                    <FileText className="mt-0.5 size-3.5 shrink-0 text-ink-400" aria-hidden="true" />
                    <span>{movimiento.observaciones}</span>
                  </p>
                </section>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-ink-500">No se ha podido leer el movimiento.</p>
          )}
        </div>
      )}
    </Dialog>
  );
}
